import dayjs from 'dayjs';
import {formatCommaNumber} from '@/utils/utils';
import {ModuSignMethod} from '@/views/ContractManagement/enums';

/**
 * 물품매매V5(개정 서식) 계약 생성 요청 바디 조립 — 순수 함수 (SPEC: v5 계약 생성 화면 신설)
 *
 * 출처: v3 의 `ContractManagementV3/payload/mulpumV6Payload.js` 복제본.
 * [HARD] 원본과의 차이는 **비고(description) 한 항목뿐**이다. 개정 서식에 비고란이 없어졌고
 * 백엔드 `DentiumProductSaleV5CreateDto` 에도 그 필드가 없다. 나머지 필드 순서·trim/replace
 * 체인·포맷 함수 호출은 한 글자도 바꾸지 않았다 — 값이 달라지면 조용한 회귀가 된다.
 *
 * state 인자는 컴포넌트 data() 필드명을 그대로 반영한 얕은 매핑이다. MulpumV5V5.vue 의
 * createContract()/makeContractDraft()/createTempContract() 가 this.* 를 모아 호출한다.
 *
 * 검증: `scripts/contracts-v3-payload-check.js`(v3 용)와 같은 방식으로 백엔드 DTO 픽스처와
 * 대조한다. V5 용 픽스처는 contracts-api 의 `__golden__/product-sale-v5-*.json` 이다.
 */

/**
 * epoch(ms)/dayjs 값을 'YYYY-MM-DD' 문자열로 변환한다. v1 getDateFromEpochTime()과 동일.
 */
export function getDateFromEpochTime(value) {
  if (!value) return '';
  return dayjs(value).format('YYYY-MM-DD');
}

/**
 * MulpumV5(개정 서식) 폼 state → POST /contracts/create-by-template (및 temp/embedded-drafts) 요청 바디.
 * v3 의 mulpumV6Payload.js 를 복제했다. 차이는 **비고(description) 한 항목뿐**이다 —
 * 개정 서식에 비고란이 없어 빼냈고, 백엔드 V5 DTO 에도 그 필드가 없다.
 */
export function buildMulpumV5Payload(state) {
  const {
    templateId,
    contractType,
    contractTitle,
    contractCreator,
    clientData,
    itemAmounts,
    promotionCode,
    discountRate,
    selectedPaymentMethod,
    cardCompany,
    selectedPaymentCondition,
    paymentMonths,
    firstPaymentDate,
    staffRows,
    excludableStaffRoles,
    attach2Items,
    confirmInfo,
    contractDate,
    totalPrice,
  } = state;

  const newAddress = `${clientData.address}`.trim();
  const cond = selectedPaymentCondition;
  const months = cond && cond.hasMonths ? String(paymentMonths || '') : '';
  const fmtItem = (v) => (v ? formatCommaNumber(v) : '');

  return {
    templateId,
    contractType,
    contractTitle,
    contractCreator,
    clientInfos: [
      {
        companyName: clientData.companyName
            .replace('(출고정지)', '')
            .replace('(거래중지_악성미수)', '')
            .replace('(거래중지-영본확인)', '')
            .replace('(거래정지)', ''),
        bizNo: clientData.cNo,
        address: newAddress,
        representative: clientData.ceo.name,
        birthday: getDateFromEpochTime(clientData.ceo.birthday),
        licenseNo: clientData.licenseNo,
        mobile: clientData.contact.mobile,
        email: clientData.contact.email,
        signMethod: ModuSignMethod.SECURE_LINK,
      }
    ],
    itemAmounts: {
      implant: fmtItem(itemAmounts.implant),
      material: fmtItem(itemAmounts.material),
      equipment: fmtItem(itemAmounts.equipment),
      etc: fmtItem(itemAmounts.etc),
    },
    promotionCode,
    discountRate: discountRate.toString(),
    paymentDetails: {
      paymentMethod: selectedPaymentMethod ? selectedPaymentMethod.name : '',
      cardCompany: selectedPaymentMethod && selectedPaymentMethod.name === '신용카드' ? cardCompany : '',
      paymentCondition: cond ? cond.name : '',
      paymentMonths: months,
      firstPaymentDate: getDateFromEpochTime(firstPaymentDate),
    },
    staffSigners: staffRows.map(r => ({
      role: r.role,
      name: r.name,
      mobile: r.mobile,
      deptName: r.deptName,
      excluded: excludableStaffRoles.includes(r.role) ? !!r.excluded : false,
    })),
    attach2Items: attach2Items.map(item => ({
      group: item.group,
      checked: !!item.checked,
      code: item.code || '',
    })),
    confirmInfo: {
      branch: confirmInfo.branch,
      contractDate: getDateFromEpochTime(contractDate),
      installDate: getDateFromEpochTime(confirmInfo.installDate),
    },
    amountNum: parseInt(totalPrice),
    requestMessage: "덴티움 물품매매계약서 송부드립니다. ",
  };
}
